import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import theme from './theme/theme';
import * as Sentry from '@sentry/react';
import { useAuthStore } from './stores/authStore';
import ErrorBoundary from './components/common/ErrorBoundary';

// ─── Lazy-loaded Pages (code-split per route) ──────────────────────────────

const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const PremiumsPage = lazy(() => import('./pages/data-entry/PremiumsPage'));
const ClaimsPage = lazy(() => import('./pages/data-entry/ClaimsPage'));
const YieldCurvePage = lazy(() => import('./pages/data-entry/YieldCurvePage'));
const UPRPage = lazy(() => import('./pages/data-entry/UPRPage'));
const DACPage = lazy(() => import('./pages/data-entry/DACPage'));
const RevenueAccountPage = lazy(() => import('./pages/data-entry/RevenueAccountPage'));
const RiskAdjustmentPage = lazy(() => import('./pages/data-entry/RiskAdjustmentPage'));
const ExpensesPage = lazy(() => import('./pages/data-entry/ExpensesPage'));
const CalculationsPage = lazy(() => import('./pages/calculations/CalculationsPage'));
const DisclosuresPage = lazy(() => import('./pages/disclosures/DisclosuresPage'));
const FinancialStatementsPage = lazy(() => import('./pages/financial-statements/FinancialStatementsPage'));
const XBRLPage = lazy(() => import('./pages/xbrl/XBRLPage'));
const CompanyInfoPage = lazy(() => import('./pages/company/CompanyInfoPage'));
const BusinessClassesPage = lazy(() => import('./pages/company/BusinessClassesPage'));
const IFRS17PoliciesPage = lazy(() => import('./pages/company/IFRS17PoliciesPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const CountriesPage = lazy(() => import('./pages/admin/CountriesPage'));
const CountryDetailPage = lazy(() => import('./pages/admin/CountryDetailPage'));
const CompaniesPage = lazy(() => import('./pages/admin/CompaniesPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const AdminYieldCurvesPage = lazy(() => import('./pages/admin/AdminYieldCurvesPage'));
const TrianglesPage = lazy(() => import('./pages/data-entry/TrianglesPage'));
const ImportExportPage = lazy(() => import('./pages/import-export/ImportExportPage'));
const SensitivityPage = lazy(() => import('./pages/disclosures/SensitivityPage'));
const ContractGroupsPage = lazy(() => import('./pages/company/ContractGroupsPage'));
const GMMDataEntryPage = lazy(() => import('./pages/data-entry/GMMDataEntryPage'));
const VFADataEntryPage = lazy(() => import('./pages/data-entry/VFADataEntryPage'));
const ReinsurancePage = lazy(() => import('./pages/data-entry/ReinsurancePage'));
const RetentionRatesPage = lazy(() => import('./pages/data-entry/RetentionRatesPage'));
const CommissionsPage = lazy(() => import('./pages/data-entry/CommissionsPage'));
const ExpenseAllocationsPage = lazy(() => import('./pages/data-entry/ExpenseAllocationsPage'));
const IFRS4BalanceSheetPage = lazy(() => import('./pages/data-entry/IFRS4BalanceSheetPage'));
const AdminMortalityTablesPage = lazy(() => import('./pages/admin/AdminMortalityTablesPage'));
const AdminCommissionSignaturesPage = lazy(() => import('./pages/admin/AdminCommissionSignaturesPage'));
const LapseTablesPage = lazy(() => import('./pages/data-entry/LapseTablesPage'));
const LifeAssumptionsPage = lazy(() => import('./pages/data-entry/LifeAssumptionsPage'));
const BalanceSheetEntryPage = lazy(() => import('./pages/data-entry/BalanceSheetEntryPage'));
const BulkUploadPage = lazy(() => import('./pages/data-entry/BulkUploadPage'));
const IFRS4ConversionPage = lazy(() => import('./pages/analysis/IFRS4ConversionPage'));
const GlossaryPage = lazy(() => import('./pages/help/GlossaryPage'));
const MyProfilePage = lazy(() => import('./pages/profile/MyProfilePage'));
const UserProfilePage = lazy(() => import('./pages/profile/UserProfilePage'));
const DirectoryPage = lazy(() => import('./pages/profile/DirectoryPage'));
const MessagesPage = lazy(() => import('./pages/messages/MessagesPage'));

const GMMReconciliationPage = lazy(() => import('./pages/disclosures/GMMReconciliationPage'));
const RiskDisclosuresPage = lazy(() => import('./pages/disclosures/RiskDisclosuresPage'));
const ClaimsDevelopmentPage = lazy(() => import('./pages/disclosures/ClaimsDevelopmentPage'));
const NarrativeDisclosuresPage = lazy(() => import('./pages/disclosures/NarrativeDisclosuresPage'));
const PeriodManagementPage = lazy(() => import('./pages/disclosures/PeriodManagementPage'));
const LTRevenueAccountPage = lazy(() => import('./pages/data-entry/LTRevenueAccountPage'));
const ApprovalsPage = lazy(() => import('./pages/approvals/ApprovalsPage'));
const CompletenessPage = lazy(() => import('./pages/common/ComingSoonPage'));
const ReserveResultsPage = lazy(() => import('./pages/analysis/ReserveResultsPage'));

const BudgetDashboardPage = lazy(() => import('./pages/budgeting/BudgetDashboardPage'));
const BudgetAssumptionsPage = lazy(() => import('./pages/budgeting/BudgetAssumptionsPage'));
const BudgetVariancePage = lazy(() => import('./pages/budgeting/BudgetVariancePage'));

const MethodSelectionPage = lazy(() => import('./pages/actuarial-model/MethodSelectionPage'));
const StochasticReservingPage = lazy(() => import('./pages/actuarial-model/StochasticReservingPage'));
const ExperienceAnalysisPage = lazy(() => import('./pages/actuarial-model/ExperienceAnalysisPage'));
const AssumptionGovernancePage = lazy(() => import('./pages/actuarial-model/AssumptionGovernancePage'));
const EngineManualPage = lazy(() => import('./pages/actuarial-model/EngineManualPage'));
const GMMPolicyInputPage = lazy(() => import('./pages/actuarial-model/GMMPolicyInputPage'));
const GMMTestRunnerPage = lazy(() => import('./pages/actuarial-model/GMMTestRunnerPage'));
const GMMComparisonPage = lazy(() => import('./pages/actuarial-model/GMMComparisonPage'));
const ProductLinkagePage = lazy(() => import('./pages/actuarial-model/ProductLinkagePage'));

// ── Reinsurance Module ────────────────────────────────────────────────────
const TreatyManagementPage = lazy(() => import('./pages/reinsurance/TreatyManagementPage'));
const PerformanceDashboardPage = lazy(() => import('./pages/reinsurance/PerformanceDashboardPage'));
const AdequacyReportPage = lazy(() => import('./pages/reinsurance/AdequacyReportPage'));

// ── Pricing Module ───────────────────────────────────────────────────────
const ProductManagementPage = lazy(() => import('./pages/pricing/ProductManagementPage'));
const ActiveProductsPage = lazy(() => import('./pages/pricing/ActiveProductsPage'));
const ActiveProductCommissionsPage = lazy(() => import('./pages/pricing/ActiveProductCommissionsPage'));
const ActiveProductExpensesPage = lazy(() => import('./pages/pricing/ActiveProductExpensesPage'));
const ActiveProductLapseRatesPage = lazy(() => import('./pages/pricing/ActiveProductLapseRatesPage'));
const PricingAnalysisPage = lazy(() => import('./pages/pricing/PricingAnalysisPage'));
const ProfitTestPage = lazy(() => import('./pages/pricing/ProfitTestPage'));
const CertificatesPage = lazy(() => import('./pages/pricing/CertificatesPage'));

// ── Life Profit Testing ─────────────────────────────────────────────────
const LifePricingDashboardPage = lazy(() => import('./pages/pricing/life/LifePricingDashboardPage'));
const LifeModelPointsPage = lazy(() => import('./pages/pricing/life/LifeModelPointsPage'));
const LifeProfitTestRunPage = lazy(() => import('./pages/pricing/life/LifeProfitTestRunPage'));
const LifeProfitTestHistoryPage = lazy(() => import('./pages/pricing/life/LifeProfitTestHistoryPage'));

// ── Actuarial Model — Active Products (read-only) ───────────────────────
const ActActiveProductsPage = lazy(() => import('./pages/actuarial-model/ActActiveProductsPage'));

// ── FCR Module ──────────────────────────────────────────────────────────
const FCRDashboardPage = lazy(() => import('./pages/fcr/FCRDashboardPage'));

// ── Policy Data ─────────────────────────────────────────────────────────
const PolicyDataDashboardPage = lazy(() => import('./pages/policy-data/PolicyDataDashboardPage'));
const PolicyDataUploadPage = lazy(() => import('./pages/policy-data/PolicyDataUploadPage'));
const ClaimsDataEntryPage = lazy(() => import('./pages/policy-data/ClaimsDataEntryPage'));
const MemberRegistryPage = lazy(() => import('./pages/policy-data/MemberRegistryPage'));
const MembershipMovementsPage = lazy(() => import('./pages/policy-data/MembershipMovementsPage'));
const CohortCategoriesPage = lazy(() => import('./pages/policy-data/CohortCategoriesPage'));
const CohortSummaryPage = lazy(() => import('./pages/policy-data/CohortSummaryPage'));
const SolvencyAnalysisPage = lazy(() => import('./pages/fcr/SolvencyAnalysisPage'));
const RiskHeatmapPage = lazy(() => import('./pages/fcr/RiskHeatmapPage'));
const FCRStressTestPage = lazy(() => import('./pages/fcr/StressTestPage'));
const FCRReportViewerPage = lazy(() => import('./pages/fcr/FCRReportViewerPage'));

// ── Life Insurance ──────────────────────────────────────────────────────
const LifeCalculationPage = lazy(() => import('./pages/calculations/LifeCalculationPage'));
const LifeDisclosuresPage = lazy(() => import('./pages/disclosures/LifeDisclosuresPage'));
const LifeContractGroupsPage = lazy(() => import('./pages/life/LifeContractGroupsPage'));
const LifeReconciliationPage = lazy(() => import('./pages/life/LifeReconciliationPage'));
const LifeSensitivityPage = lazy(() => import('./pages/life/LifeSensitivityPage'));
const LifeAssetSharePage = lazy(() => import('./pages/life/LifeAssetSharePage'));
const JobStatusPage = lazy(() => import('./pages/calculations/JobStatusPage'));

const CountryBusinessClassesPage = lazy(() => import('./pages/admin/CountryBusinessClassesPage'));

// ── Integration / Admin ─────────────────────────────────────────────────
const IntegrationSettingsPage = lazy(() => import('./pages/admin/IntegrationSettingsPage'));
const SystemMetricsPage = lazy(() => import('./pages/admin/SystemMetricsPage'));
const SystemLimitsPage = lazy(() => import('./pages/admin/SystemLimitsPage'));
const FeatureFlagsPage = lazy(() => import('./pages/admin/FeatureFlagsPage'));
const ScheduledReportsPage = lazy(() => import('./pages/admin/ScheduledReportsPage'));
const JobDashboardPage = lazy(() => import('./pages/admin/JobDashboardPage'));

// ─── Function dashboards ────────────────────────────────────────────────────
const BudgetingDashboard = lazy(() => import('./pages/budgeting/BudgetingDashboard'));
const ActuarialModelDashboard = lazy(() => import('./pages/actuarial/ActuarialModelDashboard'));
const ReinsuranceDashboard = lazy(() => import('./pages/reinsurance/ReinsuranceDashboard'));
const PricingDashboard = lazy(() => import('./pages/pricing/PricingDashboard'));
const FCRDashboard = lazy(() => import('./pages/fcr/FCRDashboard'));

// ─── Layout ─────────────────────────────────────────────────────────────────

import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/layout/ProtectedRoute';

// ─── Suspense Fallback ──────────────────────────────────────────────────────

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress size={40} sx={{ color: '#6B3FA0' }} />
    </Box>
  );
}

// ─── React Query Client ─────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        Sentry.captureException(error, {
          tags: { source: 'react-query-mutation' },
        });
      },
    },
  },
});

// ─── App Component ──────────────────────────────────────────────────────────

export default function App() {
  const loadFromStorage = useAuthStore((s) => s.loadFromStorage);

  // Rehydrate user object from /auth/me on page load/refresh
  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard — company-scoped roles (not admin/regulator) */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'executive']}>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Data Entry routes (editable — company_user + consultant_user only) */}
            <Route
              path="premiums"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <PremiumsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="claims"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <ClaimsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="yield-curves"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <YieldCurvePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="upr"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <UPRPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="dac"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <DACPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="reinsurance"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <ReinsurancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="revenue-account"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <RevenueAccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="risk-adjustment"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <RiskAdjustmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="expenses"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <ExpensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="triangles"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <TrianglesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="retention-rates"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <RetentionRatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="commissions"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <CommissionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="expense-allocations"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <ExpenseAllocationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="ifrs4-balance-sheet"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <IFRS4BalanceSheetPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="balance-sheet-entry"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <BalanceSheetEntryPage />
                </ProtectedRoute>
              }
            />

            {/* Bulk Upload — data entry roles */}
            <Route
              path="bulk-upload"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <BulkUploadPage />
                </ProtectedRoute>
              }
            />

            {/* Data Provided routes (read-only views — data entry roles + auditor) */}
            <Route
              path="provided/premiums"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <PremiumsPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/claims"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <ClaimsPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/upr"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <UPRPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/dac"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <DACPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/reinsurance"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <ReinsurancePage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/revenue-account"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <RevenueAccountPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/risk-adjustment"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <RiskAdjustmentPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/expenses"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <ExpensesPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/triangles"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <TrianglesPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/retention-rates"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <RetentionRatesPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/commissions"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <CommissionsPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/expense-allocations"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <ExpenseAllocationsPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/ifrs4-balance-sheet"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <IFRS4BalanceSheetPage readOnly />
                </ProtectedRoute>
              }
            />
            <Route
              path="provided/balance-sheet-entry"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <BalanceSheetEntryPage readOnly />
                </ProtectedRoute>
              }
            />

            {/* Calculations — run restricted to consultant + admin; view for auditor + executive */}
            <Route
              path="calculations"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user', 'company_user', 'auditor', 'executive']}>
                  <CalculationsPage />
                </ProtectedRoute>
              }
            />

            {/* Disclosures & Reports — all non-admin roles */}
            <Route
              path="disclosures"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <DisclosuresPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="financial-statements"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <FinancialStatementsPage />
                </ProtectedRoute>
              }
            />

            {/* Import / Export — data entry roles only */}
            <Route
              path="import-export"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <ImportExportPage />
                </ProtectedRoute>
              }
            />

            {/* Sensitivity & IFRS 4 Conversion */}
            <Route
              path="sensitivity"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <SensitivityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="ifrs4-conversion"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <IFRS4ConversionPage />
                </ProtectedRoute>
              }
            />

            {/* XBRL Reporting — data entry + auditor + regulator */}
            <Route
              path="xbrl"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator']}>
                  <XBRLPage />
                </ProtectedRoute>
              }
            />

            {/* VFA Data Entry */}
            <Route path="vfa-data-entry" element={<VFADataEntryPage />} />
            {/* Life Assumptions & Lapse Tables (decrement tables moved to admin) */}
            <Route path="lapse-tables" element={<LapseTablesPage />} />
            <Route path="life-assumptions" element={<LifeAssumptionsPage />} />
            {/* Life Insurance Calculations & Disclosures */}
            <Route
              path="life-calculations"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <LifeCalculationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-disclosures"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <LifeDisclosuresPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-contract-groups"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <LifeContractGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-reconciliation"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <LifeReconciliationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-sensitivity"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <LifeSensitivityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-asset-share"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <LifeAssetSharePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="job-status"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <JobStatusPage />
                </ProtectedRoute>
              }
            />
            <Route path="integration-settings" element={<IntegrationSettingsPage />} />
            <Route path="system-metrics" element={<SystemMetricsPage />} />
            <Route path="system-limits" element={<SystemLimitsPage />} />

            {/* Company Info — all company-scoped roles + regulator */}
            <Route
              path="company-info"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <CompanyInfoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="business-classes"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'executive']}>
                  <BusinessClassesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="contract-groups"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <ContractGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="ifrs17-policies"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <IFRS17PoliciesPage />
                </ProtectedRoute>
              }
            />

            {/* GMM Data Entry — data entry roles only */}
            <Route
              path="gmm-data-entry"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <GMMDataEntryPage />
                </ProtectedRoute>
              }
            />

            {/* Data Completeness — data entry + auditor */}
            <Route
              path="completeness"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor']}>
                  <CompletenessPage />
                </ProtectedRoute>
              }
            />

            {/* Claims Development (IFRS 17 Para 131) */}
            <Route path="claims-development" element={<ClaimsDevelopmentPage />} />

            {/* Narrative Disclosures (IFRS 17 Para 97-126) */}
            <Route path="narrative-disclosures" element={<NarrativeDisclosuresPage />} />

            {/* Period Management (lock/unlock) */}
            <Route path="period-management" element={<PeriodManagementPage />} />

            {/* GMM Disclosures — all report viewers */}
            <Route
              path="gmm-disclosures"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <GMMReconciliationPage />
                </ProtectedRoute>
              }
            />

            {/* Risk Disclosures — all report viewers */}
            <Route
              path="risk-disclosures"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'regulator', 'executive']}>
                  <RiskDisclosuresPage />
                </ProtectedRoute>
              }
            />

            {/* Reserve Results (read-only triangle summary) */}
            <Route path="reserve-results" element={<ReserveResultsPage />} />

            {/* Long-Term Insurance — data entry only */}
            <Route
              path="lt-revenue-account"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user']}>
                  <LTRevenueAccountPage />
                </ProtectedRoute>
              }
            />

            {/* ── Function dashboards ── */}
            <Route path="budgeting" element={<BudgetingDashboard />} />
            <Route path="actuarial-model" element={<ActuarialModelDashboard />} />
            <Route path="reinsurance-module" element={<ReinsuranceDashboard />} />
            <Route path="pricing" element={<PricingDashboard />} />
            <Route path="financial-condition-report" element={<FCRDashboard />} />

            {/* ── Budgeting Function ── */}
            <Route path="budget-dashboard" element={<BudgetDashboardPage />} />
            <Route path="budget-assumptions" element={<BudgetAssumptionsPage />} />
            <Route path="budget-variance" element={<BudgetVariancePage />} />
            {/* budgeting → BudgetingDashboard (above) */}
            {/* ── Actuarial Model Function (consultant_user + admin only) ── */}
            <Route
              path="act-method-selection"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <MethodSelectionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-stochastic"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <StochasticReservingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-experience"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <ExperienceAnalysisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-governance"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <AssumptionGovernancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-active-products"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <ActActiveProductsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-linkage"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <ProductLinkagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-engine-manual"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <EngineManualPage />
                </ProtectedRoute>
              }
            />
            {/* V12 GMM Testing */}
            <Route
              path="act-gmm-input"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <GMMPolicyInputPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-gmm-test"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <GMMTestRunnerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="act-gmm-comparison"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <GMMComparisonPage />
                </ProtectedRoute>
              }
            />
            {/* actuarial-model → ActuarialModelDashboard (above) */}
            <Route
              path="ri-treaties"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user', 'company_user']}>
                  <TreatyManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="ri-performance"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user', 'company_user']}>
                  <PerformanceDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="ri-adequacy"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <AdequacyReportPage />
                </ProtectedRoute>
              }
            />
            {/* reinsurance-module → ReinsuranceDashboard (above) */}

            {/* Active Products (Pricing function) */}
            <Route
              path="active-products"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <ActiveProductsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="active-products/commissions"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <ActiveProductCommissionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="active-products/expenses"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <ActiveProductExpensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="active-products/lapse-rates"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <ActiveProductLapseRatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="pricing-products"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user', 'company_user']}>
                  <ProductManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="pricing-analysis"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <PricingAnalysisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="pricing-profit-test"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <ProfitTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="pricing-certificates"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <CertificatesPage />
                </ProtectedRoute>
              }
            />
            {/* pricing → PricingDashboard (above) */}

            {/* ── Life Profit Testing ─────────────────────────────────────── */}
            <Route
              path="life-pricing"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <LifePricingDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-pricing/model-points"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <LifeModelPointsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-pricing/run"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <LifeProfitTestRunPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="life-pricing/history"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'consultant_user']}>
                  <LifeProfitTestHistoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="fcr-dashboard"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user', 'company_user']}>
                  <FCRDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fcr-solvency"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <SolvencyAnalysisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fcr-risk-heatmap"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <RiskHeatmapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fcr-stress-test"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <FCRStressTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fcr-report"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user', 'company_user']}>
                  <FCRReportViewerPage />
                </ProtectedRoute>
              }
            />
            {/* financial-condition-report → FCRDashboard (above) */}

            {/* Policy Data — all authenticated + RBAC per sub-page */}
            <Route path="policy-data" element={<PolicyDataDashboardPage />} />
            <Route
              path="policy-data/upload"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'company_user', 'consultant_user']}>
                  <PolicyDataUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="policy-data/claims"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'company_user', 'consultant_user']}>
                  <ClaimsDataEntryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="policy-data/members"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'company_user', 'consultant_user', 'auditor', 'executive']}>
                  <MemberRegistryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="policy-data/movements"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'company_user', 'consultant_user', 'auditor', 'executive']}>
                  <MembershipMovementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="policy-data/cohort-categories"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'company_user', 'consultant_user', 'auditor', 'executive']}>
                  <CohortCategoriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="policy-data/cohorts"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'company_user', 'consultant_user', 'auditor', 'executive']}>
                  <CohortSummaryPage />
                </ProtectedRoute>
              }
            />

            {/* Approval Workflow — data entry + auditor + executive (view) */}
            <Route
              path="approvals"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'executive']}>
                  <ApprovalsPage />
                </ProtectedRoute>
              }
            />

            {/* Help & Reference — all authenticated roles */}
            <Route path="glossary" element={<GlossaryPage />} />

            {/* Community — profiles, directory, messages */}
            <Route path="profile" element={<MyProfilePage />} />
            <Route path="profile/:userId" element={<UserProfilePage />} />
            <Route path="directory" element={<DirectoryPage />} />
            <Route
              path="messages"
              element={
                <ProtectedRoute allowedRoles={['company_user', 'consultant_user', 'auditor', 'executive']}>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes — all guarded to super_admin + admin */}
            <Route
              path="admin"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/countries"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <CountriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/countries/:countryId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <CountryDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/country-business-classes"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <CountryBusinessClassesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/companies"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <CompaniesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/yield-curves"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <AdminYieldCurvesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/mortality-tables"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <AdminMortalityTablesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/commission-signatures"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'consultant_user']}>
                  <AdminCommissionSignaturesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/feature-flags"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <FeatureFlagsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/scheduled-reports"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <ScheduledReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/job-dashboard"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <JobDashboardPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* 404 catch-all: redirect unknown paths to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
